import type { BezugKandidat } from "./bezugKandidaten";
import type { NoteRefArt, NoteRefDto } from "../../services/vorgangService";

/**
 * Wie ein Bezug im Notiztext AUSSIEHT (PO 09.09.2026) — und wie er wieder
 * verschwindet, bevor gespeichert wird.
 *
 * ══ DER ZYKLUS ═════════════════════════════════════════════════════════════
 * Gespeichert wird immer nur der schlanke Marker:
 *
 *     <span data-ref-art="mail" data-ref-id="7">Betreff</span>
 *
 * Beim Anzeigen bläst {@link hydratisiereBezuege} ihn zu dem auf, was man sehen
 * soll — bei einer Mail zur Karte mit Absender, Zeitpunkt und Anriss. Vor dem
 * Speichern dampft {@link entwaesserBezuege} ihn wieder ein.
 *
 * Der Umweg ist Absicht: Schriebe man die Karte so, wie sie aussieht, in die
 * Datenbank, stünde dort eine KOPIE von Betreff und Anriss. Sie veraltete
 * still, und beim nächsten Öffnen sähe der Nutzer einen Stand, den es nicht
 * mehr gibt. Maßgeblich ist die `refs`-Liste; das Markup ist Anzeige.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bewusst OHNE React, wie `bezugEinsetzen.ts` daneben: im contentEditable ist
 * das DOM die Quelle der Wahrheit, und diese Umformung ist der Teil, der
 * schiefgehen kann. Als freie Funktionen ist sie im jsdom prüfbar.
 */

/** Ein Bezug, so wie er im Text markiert ist. */
type Marker = { art: NoteRefArt; id: string; titel: string };

const istArt = (v: string | null): v is NoteRefArt =>
    v === "mail" ||
    v === "anruf" ||
    v === "dokument" ||
    v === "risiko" ||
    v === "vertrag" ||
    v === "person" ||
    v === "todo";

/** Art, Id und Beschriftung eines Elements; `null`, wenn es kein Bezug ist. */
function markerAus(el: Element): Marker | null {
    const art = el.getAttribute("data-ref-art");
    const id = el.getAttribute("data-ref-id");
    if (!istArt(art) || !id) return null;
    // Bei einer schon aufgeblasenen Karte steht der Betreff in der Kopfzeile,
    // beim schlanken Marker im Element selbst.
    const kopf = el.querySelector(".nb-mailkarte-betreff");
    return { art, id, titel: (kopf?.textContent ?? el.textContent ?? "").trim() };
}

/**
 * Der Kandidat zu einem Marker. Gesucht wird über `art:id` als Text, weil die
 * Id im DOM immer ein String ist, in der Kandidatenliste aber eine Zahl sein
 * kann.
 */
function kandidatZu(m: Marker, kandidaten: BezugKandidat[]): BezugKandidat | undefined {
    return kandidaten.find((k) => k.art === m.art && String(k.id) === m.id);
}

/** Beschriftung aus der Verweisliste — der Rückfall, wenn kein Kandidat da ist. */
function labelAus(m: Marker, refs: NoteRefDto[]): string | undefined {
    const treffer = refs.find((r) => r.art === m.art && String(r.id) === m.id);
    return treffer?.label?.trim() || undefined;
}

/** Die Karte einer Mail. Kein `innerHTML` — der Betreff ist fremder Text. */
function mailKarte(m: Marker, k: BezugKandidat | undefined, betreff: string): HTMLElement {
    const karte = document.createElement("span");
    karte.className = "nb-mailkarte";
    karte.setAttribute("data-ref-art", m.art);
    karte.setAttribute("data-ref-id", m.id);
    // Die Karte ist EIN Ding: hineinschreiben ginge sonst, und der Satz landete
    // zwischen Betreff und Anriss.
    karte.setAttribute("contenteditable", "false");
    karte.setAttribute("title", "Mail öffnen");

    const zeile = document.createElement("span");
    zeile.className = "nb-mailkarte-kopf";

    const icon = document.createElement("span");
    icon.className = "nb-mailkarte-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "✉";
    zeile.appendChild(icon);

    const b = document.createElement("span");
    b.className = "nb-mailkarte-betreff";
    b.textContent = betreff;
    zeile.appendChild(b);
    karte.appendChild(zeile);

    // Absender und Zeitpunkt in einer Zeile — beide fehlen, solange die
    // Kandidaten noch nicht geladen sind (Kundentagebuch lädt sie erst bei
    // Fokus). Dann trägt die Karte eben nur den Betreff.
    const meta = [k?.zusatz, k?.datum].filter(Boolean).join(" · ");
    if (meta) {
        const z = document.createElement("span");
        z.className = "nb-mailkarte-meta";
        z.textContent = meta;
        karte.appendChild(z);
    }

    if (k?.anriss) {
        const a = document.createElement("span");
        a.className = "nb-mailkarte-anriss";
        a.textContent = k.anriss;
        karte.appendChild(a);
    }

    return karte;
}

/**
 * Jeden Bezug im Feld zu seiner Darstellung machen.
 *
 * Mail → Karte, alles andere → Chip. In beiden Fällen wird das Element
 * unschreibbar: ohne das schreibt der Nutzer hinein, der Satz nimmt die Farbe
 * des Bezugs an und wandert mit ihm.
 *
 * `refs` liefert den Rückfall-Betreff, wenn die Kandidaten (noch) nichts über
 * die Mail wissen — besser ein Betreff aus der Verweisliste als eine Karte
 * ohne Beschriftung.
 */
export function hydratisiereBezuege(
    wurzel: HTMLElement,
    kandidaten: BezugKandidat[] = [],
    refs: NoteRefDto[] = [],
): void {
    for (const el of Array.from(wurzel.querySelectorAll("[data-ref-art]"))) {
        const m = markerAus(el);
        if (!m) continue;
        const k = kandidatZu(m, kandidaten);

        if (m.art !== "mail") {
            el.classList.add("nb-ref", `nb-art-${m.art}`);
            el.setAttribute("contenteditable", "false");
            // Der Titel des Kandidaten ist frischer als der gespeicherte Text —
            // ein umbenanntes Risiko soll im Satz nicht alt heißen.
            if (k?.titel && el.textContent !== k.titel) el.textContent = k.titel;
            continue;
        }

        const betreff = k?.titel || labelAus(m, refs) || m.titel || "(Kein Betreff)";
        el.replaceWith(mailKarte(m, k, betreff));
    }
}

/**
 * Die Umkehrung: aus jeder Darstellung wieder der schlanke Marker.
 *
 * Läuft auf einer LOSGELÖSTEN Kopie des Feldes, nicht auf dem Feld selbst —
 * beim Speichern schaut der Nutzer noch auf die Karte, und sie unter ihm
 * einzudampfen risse den Cursor weg.
 */
export function entwaesserBezuege(html: string): string {
    const topf = document.createElement("div");
    topf.innerHTML = html ?? "";

    for (const el of Array.from(topf.querySelectorAll("[data-ref-art]"))) {
        const m = markerAus(el);
        if (!m) continue;
        const marker = document.createElement("span");
        marker.className = `nb-ref nb-art-${m.art}`;
        marker.setAttribute("data-ref-art", m.art);
        marker.setAttribute("data-ref-id", m.id);
        marker.textContent = m.titel;
        el.replaceWith(marker);
    }

    return topf.innerHTML;
}
